import React, { useState, useEffect } from 'react';
import UserLayout from '../components/UserLayout';
import { apiClient } from '../api/api';
import Loader from '../components/Loader';
import {
  TrendingUp,
  TrendingDown,
  Plus,
  Minus,
  Equal,
  Percent,
  ShoppingCart,
  // TrendingUpDown, ArrowUpRight, ArrowDownRight, Info, Calendar,
  // LayoutGrid, PieChart as PieChartIcon, BarChart3 — imported but unused (removed)
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';
import './PnLPage.css';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { isPreviewUser } from '../utils/previewAccess';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const PnLPage = () => {
  const [data, setData] = useState({ summary: {}, trends: [], waterfall: [], expenses: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { selectedShopId, dateRange, user } = useAuth();
  const { theme } = useTheme();
  // Preview accounts only (see utils/previewAccess.js): the Gross Revenue value in the waterfall was a
  // hard-coded white, invisible on the light theme's white card.
  const waterfallNeutralColor = isPreviewUser(user) && theme === 'light' ? '#111827' : '#fff';

  useEffect(() => {
    fetchPnLMetrics();
  }, [selectedShopId, dateRange]);

  const fetchPnLMetrics = async () => {
    try {
      setLoading(true);
      let url = `/pnl-metrics?range=${dateRange}`;
      if (selectedShopId) {
        url += `&shop_id=${selectedShopId}`;
      }
      const response = await apiClient.get(url);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching P&L metrics:', error);
      setError('Failed to load data. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    }).format(num || 0);
  };

  const trendChartData = {
    labels: (data.trends || []).map(t => t.date),
    datasets: [
      {
        label: 'Gross Revenue',
        data: (data.trends || []).map(t => t.revenue),
        borderColor: '#a855f7',
        backgroundColor: 'rgba(168, 85, 247, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 0,
      },
      {
        label: 'Net Profit',
        data: (data.trends || []).map(t => t.profit),
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 0,
      }
    ]
  };

  const donutData = {
    labels: (data.expenses || []).map(e => e.label),
    datasets: [{
      data: (data.expenses || []).map(e => e.value),
      backgroundColor: (data.expenses || []).map(e => e.color),
      borderWidth: 0,
      hoverOffset: 4,
      cutout: '75%'
    }]
  };


  return (
    <UserLayout>
      {error && (
        <div style={{background:'#2d1a1a',border:'1px solid #7f1d1d',color:'#fca5a5',padding:'12px 16px',borderRadius:'8px',margin:'16px 16px 0'}}>
          {error}
        </div>
      )}
      <div className="page-relative-wrapper">
        <div className="pnl-container">
        {/* Top High-Level Metrics */}
        <div className="pnl-metrics-grid">
          <div className={`pnl-metric-card green ${loading ? 'shimmer skeleton' : ''}`}>
            <div className="pnl-card-label">Net Profit</div>
            <div className="pnl-card-value green">{formatCurrency(data.summary?.net_profit)}</div>
            <div className={`pnl-card-trend ${data.summary?.net_profit_growth?.includes('+') ? 'up' : data.summary?.net_profit_growth?.includes('-') ? 'red' : 'neutral'}`}>
              {data.summary?.net_profit_growth?.includes('+') ? <TrendingUp size={14} /> : data.summary?.net_profit_growth?.includes('-') ? <TrendingDown size={14} /> : null}
              {data.summary?.net_profit_growth || 'N/A'} <span>vs last period</span>
            </div>
          </div>
          
          <div className={`pnl-metric-card purple ${loading ? 'shimmer skeleton' : ''}`}>
            <div className="pnl-card-label">Gross Revenue</div>
            <div className="pnl-card-value purple">{formatCurrency(data.summary?.gross_revenue)}</div>
            <div className={`pnl-card-trend ${data.summary?.gross_revenue_growth?.includes('+') ? 'up' : data.summary?.gross_revenue_growth?.includes('-') ? 'down' : 'neutral'}`}>
              {data.summary?.gross_revenue_growth?.includes('+') ? <TrendingUp size={14} /> : data.summary?.gross_revenue_growth?.includes('-') ? <TrendingDown size={14} /> : null}
              {data.summary?.gross_revenue_growth || 'N/A'} <span>vs last period</span>
            </div>
          </div>

          <div className={`pnl-metric-card red ${loading ? 'shimmer skeleton' : ''}`}>
            <div className="pnl-card-label">Total Expenses</div>
            <div className="pnl-card-value red">{formatCurrency(data.summary?.total_expenses)}</div>
            <div className={`pnl-card-trend ${data.summary?.total_expenses_growth?.includes('-') ? 'up' : data.summary?.total_expenses_growth?.includes('+') ? 'red' : 'neutral'}`}>
              {data.summary?.total_expenses_growth?.includes('-') ? <TrendingUp size={14} /> : data.summary?.total_expenses_growth?.includes('+') ? <TrendingDown size={14} /> : null}
              {data.summary?.total_expenses_growth || 'N/A'} <span>vs last period</span>
            </div>
          </div>
        </div>

        {/* Trend Chart */}
        <div className={`pnl-section-card ${loading ? 'shimmer skeleton' : ''}`} style={{ marginBottom: '32px' }}>
          <div className="pnl-chart-header">
            <div className="pnl-chart-title">Revenue & Profit Trend</div>
            <div className="pnl-chart-legend">
              <div className="pnl-legend-item">
                <div className="pnl-dot" style={{ backgroundColor: '#10b981' }} /> Net Profit
              </div>
              <div className="pnl-legend-item">
                <div className="pnl-dot" style={{ backgroundColor: '#a855f7' }} /> Gross Revenue
              </div>
            </div>
          </div>
          <div style={{ height: '340px' }}>
             <Line 
               data={{
                 labels: (data.trends || []).map(t => t.date),
                 datasets: [
                   {
                     label: 'Gross Revenue',
                     data: (data.trends || []).map(t => t.revenue),
                     borderColor: '#a855f7',
                     borderWidth: 3,
                     backgroundColor: (context) => {
                       const ctx = context.chart.ctx;
                       const gradient = ctx.createLinearGradient(0, 0, 0, 300);
                       gradient.addColorStop(0, 'rgba(168, 85, 247, 0.25)');
                       gradient.addColorStop(1, 'rgba(168, 85, 247, 0)');
                       return gradient;
                     },
                     fill: true,
                     tension: 0.4,
                     pointRadius: 0,
                   },
                   {
                     label: 'Net Profit',
                     data: (data.trends || []).map(t => t.profit),
                     borderColor: '#10b981',
                     borderWidth: 3,
                     backgroundColor: (context) => {
                       const ctx = context.chart.ctx;
                       const gradient = ctx.createLinearGradient(0, 0, 0, 300);
                       gradient.addColorStop(0, 'rgba(16, 185, 129, 0.25)');
                       gradient.addColorStop(1, 'rgba(16, 185, 129, 0)');
                       return gradient;
                     },
                     fill: true,
                     tension: 0.4,
                     pointRadius: 0,
                   }
                 ]
               }} 
               options={{
                 responsive: true,
                 maintainAspectRatio: false,
                 plugins: { legend: { display: false } },
                 scales: {
                   x: { 
                    grid: { display: false }, 
                    ticks: { color: '#444', font: { size: 12 } } 
                   },
                   y: { 
                    grid: { color: 'rgba(255,255,255,0.03)', drawBorder: false }, 
                    ticks: { 
                      color: '#444',
                      font: { size: 12 },
                      callback: (value) => value >= 1000 ? (value/1000) + 'k' : value
                    } 
                   }
                 }
               }} 
             />
          </div>
        </div>

        {/* Secondary Metrics Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px', marginBottom: '32px' }} className={loading ? 'shimmer skeleton' : ''}>
          <div className="pnl-small-card">
            <div className="pnl-small-header">
              <span className="pnl-small-label">Net Margin</span>
              <Percent size={16} className="pnl-small-icon" />
            </div>
            <div className="pnl-small-value">{(data.summary?.gross_margin || 0).toFixed(2)}%</div>
            <div className={`pnl-small-trend ${data.summary?.gross_margin_growth?.includes('+') ? 'up' : data.summary?.gross_margin_growth?.includes('-') ? 'red' : 'neutral'}`}>
              {data.summary?.gross_margin_growth?.includes('+') ? <TrendingUp size={14} /> : data.summary?.gross_margin_growth?.includes('-') ? <TrendingDown size={14} /> : null}
              {data.summary?.gross_margin_growth || 'N/A'} <span>vs last period</span>
            </div>
          </div>

          <div className="pnl-small-card">
            <div className="pnl-small-header">
              <span className="pnl-small-label">ROAS</span>
              <TrendingUp size={16} className="pnl-small-icon" />
            </div>
            <div className="pnl-small-value">{(data.summary?.roas || 0).toFixed(2)}x</div>
            <div className={`pnl-small-trend ${data.summary?.roas_growth?.includes('+') ? 'up' : data.summary?.roas_growth?.includes('-') ? 'red' : 'neutral'}`}>
              {data.summary?.roas_growth?.includes('+') ? <TrendingUp size={14} /> : data.summary?.roas_growth?.includes('-') ? <TrendingDown size={14} /> : null}
              {data.summary?.roas_growth || 'N/A'} <span>vs last period</span>
            </div>
          </div>


          <div className="pnl-small-card">
            <div className="pnl-small-header">
              <span className="pnl-small-label">AOV</span>
              <ShoppingCart size={16} className="pnl-small-icon" />
            </div>
            <div className="pnl-small-value">{formatCurrency(data.summary?.aov)}</div>
            <div className={`pnl-small-trend ${data.summary?.aov_growth?.includes('+') ? 'up' : data.summary?.aov_growth?.includes('-') ? 'red' : 'neutral'}`}>
              {data.summary?.aov_growth?.includes('+') ? <TrendingUp size={14} /> : data.summary?.aov_growth?.includes('-') ? <TrendingDown size={14} /> : null}
              {data.summary?.aov_growth || 'N/A'} <span>vs last period</span>
            </div>
          </div>
        </div>

        {/* Waterfall and Breakdown Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px', marginBottom: '32px' }} className={loading ? 'shimmer skeleton' : ''}>
          {/* Profit Waterfall */}
          <div className="pnl-section-card">
            <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '24px' }}>Profit Waterfall</div>
            <div className="pnl-waterfall-list">
              {(data.waterfall || []).map((item, idx) => {
                const iconType = item.type === 'plus' ? 'plus' : item.type === 'minus' ? 'minus' : 'total';
                const Icon = item.type === 'plus' ? Plus : item.type === 'minus' ? Minus : Equal;
                return (
                  <div key={idx} className={`pnl-waterfall-item ${item.type === 'total' ? 'active' : ''}`}>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                      <div className={`pnl-icon-circle ${iconType}`}>
                        <Icon size={16} />
                      </div>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '600' }}>{item.label}</div>
                        <div style={{ fontSize: '11px', color: '#666' }}>{item.sub}</div>
                      </div>
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: item.type === 'minus' ? '#ef4444' : item.type === 'total' ? '#10b981' : waterfallNeutralColor }}>
                      {item.type === 'minus' ? '-' : ''}{formatCurrency(item.value)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Cost Breakdown */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
             <div className="pnl-section-card">
                <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '24px' }}>Cost Breakdown</div>
                <div style={{ height: '220px', position: 'relative', display: 'flex', justifyContent: 'center' }}>
                    <Doughnut data={donutData} options={{ maintainAspectRatio: false, plugins: { legend: { display: false } } }} />
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                       <div style={{ fontSize: '12px', color: '#666' }}>Total Expenses</div>
                       <div style={{ fontSize: '18px', fontWeight: '700' }}>{formatCurrency(data.summary?.total_expenses)}</div>
                    </div>
                </div>
             </div>

             <div className="pnl-section-card">
                <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '20px' }}>Expense Details</div>
                {(data.expenses || []).map((exp, idx) => (
                  <div key={idx} className="pnl-expense-item">
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <div className="pnl-dot" style={{ backgroundColor: exp.color }} />
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: '600' }}>{exp.label}</div>
                        <div style={{ fontSize: '11px', color: '#666' }}>{exp.percentage?.toFixed(2)}% of expenses</div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '14px', fontWeight: '700' }}>{formatCurrency(exp.value)}</div>
                      <div style={{ fontSize: '11px', color: '#666' }}>{data.summary?.gross_revenue ? (exp.value / data.summary.gross_revenue * 100).toFixed(2) : '0.00'}% of revenue</div>
                    </div>
                  </div>
                ))}
             </div>
          </div>
        </div>
        </div>
      </div>
    </UserLayout>
  );
};

export default PnLPage;
